import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  type UserlandWallet,
  type WireLimitOrder,
  fetchLimitOrders,
  patchLimitOrderRemote,
  putLimitOrder,
} from '../api/userland'
import {
  type LimitOrder,
  type PlaceLimitOrderInput,
  loadLimitOrders,
  newLimitOrder,
  replaceLimitOrders,
  saveLimitOrder,
} from '../lib/limitOrders'
import { type LimitSignerVault, enrollPrimaryLimitSigner } from '../lib/limitSigner'
import { LIMIT_EXECUTION_EVENT } from './useLimitExecutionNotifications'

function toWire(order: LimitOrder): WireLimitOrder {
  return {
    id: order.id,
    wallet: order.wallet,
    pair_addr: order.pairAddr,
    base_token: order.baseToken,
    ticker: order.ticker,
    dex: order.dex,
    side: order.side,
    amount_label: order.amountLabel,
    offer_nano: order.offerNano,
    limit_mcap_usd: order.limitMcapUsd,
    limit_price_ton: order.limitPriceTon,
    slippage_bps: order.slippageBps,
    retry_policy: order.retryPolicy?.enabled ? order.retryPolicy : undefined,
    preset_index: order.presetIndex,
    buy_slippage_bps: order.buySlippageBps,
    sell_slippage_bps: order.sellSlippageBps,
    status: order.status,
    fail_reason: order.failReason,
    tx_hash: order.txHash,
    triggered_mcap_usd: order.triggeredMcapUsd,
    created_at: order.createdAt,
    updated_at: order.updatedAt,
  }
}

function fromWire(row: WireLimitOrder): LimitOrder {
  return {
    id: row.id,
    wallet: row.wallet,
    pairAddr: row.pair_addr,
    baseToken: row.base_token,
    ticker: row.ticker,
    dex: row.dex,
    side: row.side,
    amountLabel: row.amount_label,
    offerNano: row.offer_nano,
    limitMcapUsd: row.limit_mcap_usd,
    limitPriceTon: row.limit_price_ton,
    slippageBps: row.slippage_bps,
    retryPolicy: row.retry_policy,
    retryState: row.retry_state,
    presetIndex: row.preset_index,
    buySlippageBps: row.buy_slippage_bps,
    sellSlippageBps: row.sell_slippage_bps,
    status: row.status,
    failReason: row.fail_reason,
    txHash: row.tx_hash,
    triggeredMcapUsd: row.triggered_mcap_usd,
    filledMcapUsd: row.filled_mcap_usd,
    filledAt: row.filled_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/** What a refused cancel tells the user: the server's book decides, and an
 *  order that is filling or filled says so — never "unexpected error". */
export function cancelRefusal(err: unknown): Error {
  if (err instanceof ApiError) {
    if (err.code === 'already_filled')
      return new Error('Already filled — it can no longer be canceled')
    if (err.code === 'execution_pending')
      return new Error('Filling now — it can no longer be canceled. Its result shows in Activity')
    if (err.code === 'not_found') return new Error('This order is no longer in your book')
  }
  return err instanceof Error ? err : new Error('Could not cancel the limit order')
}

function notifyLimitBookChanged(): void {
  try {
    const ch = new BroadcastChannel('wils-limit-book')
    ch.postMessage('changed')
    ch.close()
  } catch {
    /* BroadcastChannel missing in some test/jsdom */
  }
}

const BOOK_POLL_MS = 4_000

/** One user intent: these inputs on this wallet. */
export function limitIntentKey(
  wallet: string,
  input: Omit<PlaceLimitOrderInput, 'wallet'>
): string {
  return JSON.stringify({ wallet, ...input, offerNano: input.offerNano.toString() })
}

export function useLimitOrders(
  wallet: UserlandWallet | null,
  vault: LimitSignerVault
): {
  orders: LimitOrder[]
  reload: () => Promise<void>
  place: (input: Omit<PlaceLimitOrderInput, 'wallet'>) => Promise<LimitOrder>
  cancel: (id: string) => Promise<void>
  openForPair: (pairAddr: string) => LimitOrder[]
} {
  const [orders, setOrders] = useState<LimitOrder[]>([])
  const walletAddress = wallet?.address ?? null
  // A read that lands after the hook is gone sets nothing (CI 2026-11-04: a
  // fetch failing after the trade stand unmounted threw "window is not
  // defined" out of setOrders, an unhandled rejection that failed the shard).
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // The server's book is the only truth (2026-11-04, live): a cached row never
  // shadows it and is never sent back. Merging the cache in let a stale "open"
  // (a client clock ahead of the server's) cover a cancel or a fill, and after
  // a re-login into another account it re-sent the previous account's book as
  // the new account's orders. The cache only answers while the server cannot,
  // and only with this wallet's rows.
  const reload = useCallback(async () => {
    let remote: LimitOrder[]
    try {
      remote = (await fetchLimitOrders()).map(fromWire)
    } catch {
      if (!walletAddress) return
      const local = await loadLimitOrders(walletAddress).catch(() => [] as LimitOrder[])
      if (mounted.current) setOrders(local)
      return
    }
    await replaceLimitOrders(remote).catch(() => {})
    if (mounted.current) setOrders(remote)
  }, [walletAddress])

  useEffect(() => {
    if (!walletAddress) {
      setOrders([])
      return
    }
    void reload()
    const onShow = () => {
      if (document.visibilityState === 'hidden') return
      void reload()
    }
    window.addEventListener('focus', onShow)
    window.addEventListener(LIMIT_EXECUTION_EVENT, onShow)
    document.addEventListener('visibilitychange', onShow)
    const poll = window.setInterval(onShow, BOOK_POLL_MS)
    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('wils-limit-book')
      ch.onmessage = () => {
        void reload()
      }
    } catch {
      ch = null
    }
    return () => {
      window.removeEventListener('focus', onShow)
      window.removeEventListener(LIMIT_EXECUTION_EVENT, onShow)
      document.removeEventListener('visibilitychange', onShow)
      window.clearInterval(poll)
      ch?.close()
    }
  }, [reload, walletAddress])

  // One intent, one order (review of #1289). An intent is these inputs on
  // this wallet. While it is being placed, a press with the same inputs joins
  // it, and a press with other inputs is refused with a message, never
  // dropped in silence. An intent whose PUT did not come back (a timeout: the
  // server may hold it) keeps its order id, so pressing again re-sends the
  // SAME order — the server's PUT is idempotent by id — never a second one.
  const placing = useRef<{ key: string; run: Promise<LimitOrder> } | null>(null)
  const unsent = useRef<{ key: string; draft: LimitOrder } | null>(null)
  const place = useCallback(
    (input: Omit<PlaceLimitOrderInput, 'wallet'>) => {
      if (!wallet) return Promise.reject(new Error('Log in to place a limit order'))
      const key = limitIntentKey(wallet.address, input)
      const inFlight = placing.current
      if (inFlight) {
        if (inFlight.key === key) return inFlight.run
        return Promise.reject(new Error('A limit order is being placed — wait for it to finish'))
      }
      const run = (async () => {
        await enrollPrimaryLimitSigner(wallet, vault)
        const draft =
          unsent.current?.key === key
            ? unsent.current.draft
            : newLimitOrder({ ...input, wallet: wallet.address })
        unsent.current = { key, draft }
        // A local cache row cannot prove that the server accepted an executable order.
        const order = fromWire(await putLimitOrder(toWire(draft)))
        if (unsent.current?.key === key) unsent.current = null
        await saveLimitOrder(order).catch(() => {})
        if (mounted.current)
          setOrders((current) => [order, ...current.filter((row) => row.id !== order.id)])
        notifyLimitBookChanged()
        return order
      })()
      placing.current = { key, run }
      const done = () => {
        if (placing.current?.run === run) placing.current = null
      }
      run.then(done, done)
      return run
    },
    [vault, wallet]
  )

  const cancel = useCallback(
    async (id: string) => {
      // A claimed order may already have been sent. The server must accept the
      // cancellation before its cache can say canceled; a 409 must never become
      // PUT. A refused cancel shows the server's book (filled, filling) at once.
      let next: LimitOrder
      try {
        next = fromWire(await patchLimitOrderRemote(id, { status: 'canceled' }))
      } catch (err) {
        void reload()
        throw cancelRefusal(err)
      }
      await saveLimitOrder(next).catch(() => {})
      if (mounted.current)
        setOrders((current) => current.map((row) => (row.id === id ? next : row)))
      notifyLimitBookChanged()
    },
    [reload]
  )

  const openForPair = useCallback(
    (pairAddr: string) =>
      orders.filter((row) => row.pairAddr === pairAddr && row.status === 'open'),
    [orders]
  )

  return useMemo(
    () => ({ orders, reload, place, cancel, openForPair }),
    [cancel, openForPair, orders, place, reload]
  )
}
